import { Loader } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useLayoutEffect, useMemo } from 'react';
import { getAgentData, useAgentMainData } from '@/modules/agent/states';
import { AGENT_TYPES, PolicyAgentMainData } from '@/modules/agent/types';
import { useControlEvents } from '@/modules/agent/hooks/useControlEvents';
import { useControlEditStatus } from '@/modules/agent/hooks/useControlEditStatus';
import { useLoggedInMember } from '@/modules/auth/states';
import { useControlViewersStore } from '@/modules/agent/states/control-viewers';
import UserAvatarGroup from '@/shared/components/avatar-group/userAvatarGroup';

interface EditorHeaderProps {
  title: string;
  confidence: 'high' | 'medium' | 'low';
}

const EditorHeader = ({ title, confidence }: EditorHeaderProps) => {
  const { id } = useParams<{ id: string }>();
  const loggedInUser = useLoggedInMember();
  const { editorsMap } = useControlViewersStore();

  // Map confidence levels to colors and labels
  const confidenceConfig = {
    high: { color: '#4CAF50', label: 'High Confidence' },
    medium: { color: '#FFC107', label: 'Medium Confidence' },
    low: { color: '#F44336', label: 'Low Confidence' },
  };

  // Use reactive hook to get agent main data
  const mainData = useAgentMainData(id || '');
  const agentData = getAgentData(id || '');
  
  // Get control ID for policy management
  const controlId = useMemo(() => {
    if (agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT) {
      // For policy management, get the actual controlId from editorsMap to match socket events
      const sessionEditors = editorsMap.get(id || '');
      if (sessionEditors && sessionEditors.size > 0) {
        // Get the first (and should be only) controlId from the editorsMap
        const actualControlId = Array.from(sessionEditors.keys())[0];
        return actualControlId;
      }
      
      // Fallback to mainData if editorsMap is empty
      const policyMainData = mainData as PolicyAgentMainData;
      const computedControlId = policyMainData?.controlId || policyMainData?.policyId || 'policy-editor';
      return computedControlId;
    }
    return null;
  }, [agentData?.agentType, mainData, editorsMap, id]);

  // Set up control events for session tracking
  const { emitControlOpened, emitControlClosed } = useControlEvents(
    id || '',
    controlId,
    loggedInUser?.member_id
  );

  // Track control edit status
  const { isBeingEdited, editorName } = useControlEditStatus(id || '', controlId || '');

  // Emit control opened/closed events
  useLayoutEffect(() => {
    if (controlId && loggedInUser?.member_id && agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT) {
      emitControlOpened();
      return () => {
        emitControlClosed();
      };
    }
  }, [controlId, loggedInUser?.member_id, agentData?.agentType, emitControlOpened, emitControlClosed]);

  const lastEditedField = mainData?.lastEditedField;
  const isEdited = agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT && 
                   (mainData as any)?.isEdited;
  
  // Show spinner if lastEditedField exists and this is a policy management agent
  const showAutoSave = !!(lastEditedField && agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT);
  

  return (
    <div className="flex flex-col border-b border-[#E4E4E7] pl-2">
      <div className="flex items-center justify-between px-4 py-4">
        <div className="flex items-center gap-2">
          <h1 className="text-[15px] font-medium text-[#171717]">{title}</h1>
          
          <div className="flex items-center ml-2">
            <div className="flex items-center text-[10px] bg-white py-[2px] rounded text-[#67657A] border px-2 border-[#E4E4E7]">
              <span 
                className="rounded min-w-2 min-h-2 inline-block text-center p-[2px] mr-[4px]"
                style={{ background: confidenceConfig[confidence].color }}
              />
              {confidenceConfig[confidence].label}
            </div>
          </div>
          
          {isEdited && !showAutoSave && (
            <p className="text-tiny text-[#A5A5A5]">Edited</p>
          )}
          
          {showAutoSave && (
            <span className="flex items-center gap-1">
              <Loader className="w-4 h-4 animate-spin" />
              <p className="text-tiny text-[#A5A5A5] text-[8px]">Saving</p>
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* User Avatar Group for showing who's on the session */}
          {controlId && (
            <UserAvatarGroup 
              sessionId={id || ''} 
              control={controlId} 
              className={isBeingEdited ? "p-1 rounded-full bg-gradient-to-r from-[#FF6496] to-[#812DD6]" : ""}
            />
          )}
        </div>
      </div>
      
      {/* Warning banner when being edited - full width below main header */}
      {isBeingEdited && (
        <div className="bg-[#FFF3E0] h-[32px] mx-4 mb-3 flex items-center justify-center rounded-md">
          <p className="text-xs text-[#62420E]">
            This response is being edited by {editorName}. New edits from you might be lost.
          </p>
        </div>
      )}
    </div>
  );
};

export default EditorHeader; 